import { createContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { env } from '@/config/env';
import type { PrismInboundMessage } from './messages';

export type PrismAuthStatus = 'pending' | 'ready' | 'standalone';

/** The raw auth state synced from Prism (or the dev override when standalone). */
interface PrismAuthState {
  token: string | null;
  orgId: string | null;
  status: PrismAuthStatus;
}

export interface PrismAuth extends PrismAuthState {
  /**
   * True once BOTH the token and the org id are available — i.e. an authenticated API call
   * can be made. Data hooks should gate `enabled` on this, not the token alone: the request
   * also needs the org id, and the two can arrive in separate messages (or the org can be
   * null on an early `prismContext`), which otherwise causes a premature 400 + error page.
   */
  isReady: boolean;
}

export const PrismBridgeContext = createContext<PrismAuth | undefined>(undefined);

function detectEmbedded(): boolean {
  try {
    return window.parent !== window.self;
  } catch {
    // Accessing a cross-origin parent throws — which means we are embedded.
    return true;
  }
}

export interface PrismBridgeProviderProps {
  children: ReactNode;
  /** Override iframe detection (tests). Defaults to real detection. */
  embedded?: boolean;
  /** Parent window to post the ready ping to (tests). Defaults to window.parent. */
  parentWindow?: Window;
}

/**
 * Receives the OptiID token + active org id from the Prism shell over
 * window.postMessage and exposes them via context. Validates the message origin
 * against an allowlist. When not embedded in Prism (standalone dev), falls back
 * to the VITE_PRISM_* dev-override env vars. See OCP-2056 / design doc 2.9.
 */
export function PrismBridgeProvider({ children, embedded, parentWindow }: PrismBridgeProviderProps) {
  const isEmbedded = embedded ?? detectEmbedded();

  const [auth, setAuth] = useState<PrismAuthState>(() =>
    isEmbedded
      ? { token: null, orgId: null, status: 'pending' }
      : { token: env.devToken ?? null, orgId: env.devOrgId ?? null, status: 'standalone' },
  );

  useEffect(() => {
    if (!isEmbedded) return;
    const parent = parentWindow ?? window.parent;

    const handleMessage = (event: MessageEvent) => {
      if (!env.prismAllowedOrigins.includes(event.origin)) return;
      const data = event.data as PrismInboundMessage | null;
      if (!data || typeof data !== 'object' || !('method' in data)) return;

      if (data.method === 'prismContext') {
        setAuth({
          token: data.params?.originalUser?.optiIdAccessToken ?? null,
          orgId: data.params?.organizationId ?? null,
          status: 'ready',
        });
      } else if (data.method === 'optiIdTokenUpdated') {
        setAuth((prev) => ({ ...prev, token: data.params?.token ?? prev.token }));
      }
    };

    window.addEventListener('message', handleMessage);
    // Signal readiness so Prism (re)posts prismContext — covers the iframe-load /
    // listener-mount race. Posted to the known Prism origins (not '*'): only a parent whose
    // origin matches receives it, so no non-Prism embedder is pinged.
    for (const origin of env.prismAllowedOrigins) {
      parent.postMessage({ method: 'ready' }, origin);
    }

    return () => window.removeEventListener('message', handleMessage);
    // Mount-only: embedded/parent are fixed for the provider's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<PrismAuth>(
    () => ({ ...auth, isReady: Boolean(auth.token) && Boolean(auth.orgId) }),
    [auth],
  );

  return <PrismBridgeContext.Provider value={value}>{children}</PrismBridgeContext.Provider>;
}
